import styled from '@emotion/styled';
import { MenuItem } from '@mui/material';
import { Divider, Typography } from '@kc-console/common-ui';
import { TOKEN_SCOPE } from '@kc-console/context';
import { np } from '@kc-console/core';

import { SCOPE_ORDER } from '../../../../constants';
import { useScopeLabel } from '../../../../hooks';

import type { MouseEvent } from 'react';
import type { Menus } from '@kc-console/context';

// event 는 navigation 진입점에서 modifier+click 감지에 사용된다.
type HoverMenuItemClickHandler = (menu: Menus, event: MouseEvent<HTMLLIElement>) => void;

interface MenuTreeProps {
  menus: Menus[];
  onClick: HoverMenuItemClickHandler;
  depthOffset?: number;
}

interface InnerMenuItemProps {
  menu: Menus;
  onClick: HoverMenuItemClickHandler;
  depthOffset?: number;
}

const InnerMenuItem = ({ menu, onClick, depthOffset = 0 }: InnerMenuItemProps) => {
  const { level, subMenus, attribute } = menu;
  const isSubtitle = attribute?.isSubtitle;
  const depth = level + depthOffset;
  const hasSubMenus = subMenus.length > 0;

  if (isSubtitle) {
    return (
      <StyledScopeHeader depth={depth}>
        <Typography variant={'label/medium/md'} color={'text/weaker/default'}>
          {menu.name}
        </Typography>
      </StyledScopeHeader>
    );
  }

  return (
    <StyledMenuItem
      depth={depth}
      disabled={hasSubMenus}
      isDisableLink={hasSubMenus}
      onClick={hasSubMenus ? undefined : event => onClick(menu, event)}
    >
      <Typography
        variant={'label/regular/md'}
        color={hasSubMenus ? 'text/weaker/default' : 'text/default'}
      >
        {menu.name}
      </Typography>
    </StyledMenuItem>
  );
};

const MenuTreeItems = ({ menus, onClick, depthOffset = 0 }: MenuTreeProps) => {
  return (
    <>
      {menus.map(menu => (
        <div key={menu.name}>
          <InnerMenuItem menu={menu} onClick={onClick} depthOffset={depthOffset} />
          {menu.subMenus.length > 0 && (
            <MenuTreeItems menus={menu.subMenus} onClick={onClick} depthOffset={depthOffset} />
          )}
        </div>
      ))}
    </>
  );
};

const HoverMenuTree = ({ menus, onClick }: MenuTreeProps) => {
  const scopeLabel = useScopeLabel();
  const hasSubtitleItems = menus.some(m => m.attribute?.isSubtitle);
  const hasProject = menus.some(m => m.scope === TOKEN_SCOPE.PROJECT);
  const hasDomain = menus.some(m => m.scope === TOKEN_SCOPE.DOMAIN);

  if (!hasSubtitleItems && hasProject && hasDomain) {
    return (
      <>
        {SCOPE_ORDER.map((scope, index) => {
          const items = menus.filter(m => m.scope === scope);
          if (items.length === 0) return null;

          const isFirst = SCOPE_ORDER.slice(0, index).every(
            s => menus.filter(m => m.scope === s).length === 0,
          );

          return (
            <div key={scope}>
              {!isFirst && <Divider variant="middle" margin={4} />}
              <StyledScopeHeader depth={0}>
                <Typography variant={'label/semibold/xs'} color={'text/weakest/default'}>
                  {scopeLabel[scope]}
                </Typography>
              </StyledScopeHeader>
              <MenuTreeItems menus={items} onClick={onClick} />
            </div>
          );
        })}
      </>
    );
  }

  return <MenuTreeItems menus={menus} onClick={onClick} />;
};

export default HoverMenuTree;

const StyledScopeHeader = styled('div', {
  shouldForwardProp: np('depth'),
})<{ depth: number }>`
  min-width: 200px;
  height: 32px;
  display: flex;
  align-items: center;
  padding: ${({ depth }) => (depth > 0 ? `6px 16px 6px ${16 + 8 * depth}px` : '6px 16px')};
`;

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: np('depth', 'isDisableLink'),
})<{ depth: number; isDisableLink: boolean }>`
  min-width: 200px;
  height: 32px;
  padding: ${({ depth }) => {
    return depth > 0 ? `6px 16px 6px ${16 + 8 * depth}px` : '6px 16px';
  }};
  opacity: 1;
`;
